import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import EnquiryForm from "./components/EnquiryForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faFacebook } from "@fortawesome/free-brands-svg-icons";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import {
  BookOpen,
  Clock,
  Eye,
  Target,
  TrendingUp,
  UserCheck,
  MapPin,
  Zap,
  CheckCircle,
  Phone,
  Mail,
  VideoIcon,
} from "lucide-react";
import MovingServices from "./components/MovingServices";

const Counter = ({ target, suffix = "", start }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime;
    const duration = 2000; // animation time (2s)

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);

      const value = Math.floor(percentage * target);
      setCount(value);

      if (percentage < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [start, target]);

  return (
    <h2 className="counter-number">
      {count.toLocaleString()}
      {suffix}
    </h2>
  );
};
const properties = [
  {
    id: 1,
    name: "Vrindavan by Namishree",
    location: "Kondapur, Hyderabad",
    price: "₹1.2 Cr - ₹2.1 Cr",
    image: "/images/vrindavan.png",
    description:
      "Premium gated community offering modern apartments with top-class amenities and excellent connectivity.",
  },
  {
    id: 2,
    name: "Primark Inspira",
    location: "Miyapur, Hyderabad",
    price: "₹97L - ₹1.86 Cr",
    image: "/images/primark.png",
    description:
      "Thoughtfully designed homes with spacious layouts, ideal for families seeking comfort and convenience.",
  },
  {
    id: 3,
    name: "Elegant Nivasa",
    location: "Tellapur",
    price: "₹96L onwards",
    image: "/images/nivasa.png",
    description:
      "Affordable luxury apartments in a fast-growing location with strong investment potential.",
  },
  {
    id: 4,
    name: "Indis Viva City",
    location: "Kondapur",
    price: "₹1.26 Cr onwards",
    image: "/images/viva.png",
    description:
      "High-end residential project featuring premium amenities and proximity to IT hubs.",
  },
  {
    id: 5,
    name: "Sia Prospera ",
    location: "Miyapur",
    price: "1.79 - 2.58 Cr",
    image: "/images/sia.png",
    description:
      "Luxury apartments with world-class amenities, designed for discerning buyers seeking a premium lifestyle.",
  },
];

function App() {
  const [startCount, setStartCount] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const ref = statsRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartCount(true);
        }
      },
      { threshold: 0.4 }
    );

    if (ref) observer.observe(ref);

    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, []);

  return (
    <div className="app">
      {/* NAVBAR */}
      <header className="navbar">
        <div className="logo">
          <img src="/logo.png" alt="" />
        </div>

        <div className="nav-right">
          <ul className="nav-links">
            <li>
              <a href="#home">Home</a>
            </li>
            <li>
              <a href="#about">About</a>
            </li>
            <li>
              <a href="#services">Services</a>
            </li>
            <li>
              <a href="#whyus">Why Us</a>
            </li>
          </ul>

          <a href="#properties" className="buy-btn">
            Buy
          </a>
        </div>
      </header>
      <section className="hero" id="home">
        <span className="badge">PERFORMANCE-DRIVEN REAL ESTATE PARTNER</span>

        <h1>
          Accelerating <span>Real Estate Sales</span>
        </h1>

        <h3 className="hero-subtitle">
          Accelerating Real Estate Sales Through Qualified Demand
        </h3>

        <p>
          Connecting developers with serious, qualified buyers through
          structured engagement and conversion-focused strategies.
        </p>

        <div className="hero-buttons">
          <a href="#properties" className="">
            <button className="primary-btn">Buy →</button>
          </a>

          <a href="#services" className="secondary-btn">
            Our Services
          </a>
        </div>

        <div className="stats-section" ref={statsRef}>
          <div>
            <Counter target={30000} suffix="+" start={startCount} />
            <p>Qualified Leads</p>
          </div>
          <div>
            <Counter target={25} suffix="+" start={startCount} />
            <p>Projects Marketed</p>
          </div>
          {/* <div>
            <Counter target={85} suffix="%" start={startCount} />
            <p>Conversion Rate</p>
          </div> */}
          <div>
            <Counter target={98} suffix="%" start={startCount} />
            <p>Client Satisfaction</p>
          </div>
        </div>
      </section>
      <section className="about-section" id="about">
        {/* <p className="section-tag">ABOUT US</p> */}
        <h2 className="section-heading">Who We Are</h2>
        <div className="gold-line"></div>

        <div className="about-grid">
          <div className="about-card">
            <div className="about-icon">
              <BookOpen />
            </div>
            <h3>Our Introduction</h3>
            <p>
              Presignia Realty is a performance-driven real estate partner
              dedicated to accelerating project sales through qualified demand
              and structured buyer engagement. Our approach focuses on
              delivering serious buyers, improving conversion efficiency, and
              enabling developers to achieve faster and more predictable sales
              outcomes.
            </p>
          </div>

          <div className="about-card">
            <div className="about-icon">
              <Clock />
            </div>
            <h3>Our Journey</h3>
            <p>
              Presignia Realty was built on practical experience in real estate
              buyer engagement and site visit-driven sales. Recognizing the gap
              between unqualified demand and actual conversions, the company was
              established to bring a more structured, performance-driven
              approach to real estate sales and demand generation.
            </p>
          </div>

          <div className="about-card">
            <div className="about-icon">
              <Eye />
            </div>
            <h3>Our Vision</h3>
            <p>
              To build a scalable real estate demand generation ecosystem that
              bridges the gap between developers and genuine buyers, creating
              faster, more efficient, and conversion-focused sales processes.
            </p>
          </div>

          <div className="about-card">
            <div className="about-icon">
              <Target />
            </div>
            <h3>Our Mission</h3>
            <p>
              To connect serious property buyers with the right real estate
              opportunities through structured demand generation, professional
              buyer engagement, and seamless site visit coordination, helping
              developers accelerate sales and achieve faster closures.
            </p>
          </div>
        </div>
      </section>
      <section className="services-section" id="services">
        {/* <p className="section-tag">WHAT WE DO</p> */}
        <h2 className="section-heading">Our Services</h2>
        <div className="gold-line"></div>

        <div className="services-grid">
          <div className="service-card">
            <div className="service-icon">
              <TrendingUp />
            </div>
            <h3>Demand Generation</h3>
            <p>
              Generating qualified buyer leads through targeted outreach focused
              on serious property seekers.
            </p>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <UserCheck />
            </div>
            <h3>Buyer Qualification</h3>
            <p>
              Filtering leads based on budget, location, and intent to ensure
              high-quality prospects.
            </p>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <MapPin />
            </div>
            <h3>Site Visit Coordination</h3>
            <p>
              Arranging and managing site visits for interested buyers to
              improve engagement and decision-making.
            </p>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <Zap />
            </div>
            <h3>Sales Acceleration</h3>
            <p>
              Supporting developers in converting demand into bookings through
              structured buyer engagement.
            </p>
          </div>
        </div>
      </section>
      <section className="why-section" id="whyus">
        {/* <p className="section-tag">WHY PARTNER WITH US</p> */}
        <h2 className="section-heading">Why Choose Us</h2>
        <div className="gold-line"></div>

        <p className="why-subtext">
          We don't just generate leads – we drive real estate sales outcomes.
        </p>

        <div className="why-grid">
          <div className="why-card">
            <CheckCircle />
            <div>
              <h3>Qualified Buyers Only</h3>
              <p>
                We focus on bringing serious, intent-driven buyers actively
                looking for property.
              </p>
            </div>
          </div>

          <div className="why-card ">
            <CheckCircle />
            <div>
              <h3>Structured Sales Approach</h3>
              <p>
                Our systematic process ensures consistent and predictable sales
                outcomes.
              </p>
            </div>
          </div>

          <div className="why-card">
            <CheckCircle />
            <div>
              <h3>Strong Market Understanding</h3>
              <p>
                Deep knowledge of real estate market dynamics and buyer
                behavior.
              </p>
            </div>
          </div>

          <div className="why-card">
            <CheckCircle />
            <div>
              <h3>Conversion-Focused Execution</h3>
              <p>
                Every action is designed to maximize conversion rates and close
                deals faster.
              </p>
            </div>
          </div>

          <div className="why-card">
            <CheckCircle />
            <div>
              <h3>Professional Coordination</h3>
              <p>
                Seamless management of buyer engagement from first contact to
                final closure.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="contact-section" id="connect">
        {/* <p className="section-tag">CONTACT US</p> */}
        <h2 className="section-heading">Let's Connect</h2>
        <div className="gold-line"></div>

        <p className="why-subtext">
          Ready to accelerate your real estate sales? Connect with us today.
        </p>
        <div className="wrapper">
          {/* LEFT */}
          <div className="card-left">
            <div className="image-box">
              <img src="/profile.png" alt="Prem Singh" />
              <div className="overlay" />

              <div className="profile-text">
                <h2>Prem Singh</h2>
                <h4>Founder & Managing Head</h4>
                <p>
                  Leading Presignia Realty with a focus on demand generation,
                  buyer engagement, and real estate sales acceleration.
                </p>
              </div>
            </div>

            <div className="content">
              <h3>About</h3>
              <p>
                With extensive experience in real estate sales and demand
                generation, Prem Singh has built Presignia Realty from the
                ground up.
              </p>

              <h3>Expertise</h3>
              <div className="grid">
                <ul>
                  <li>Real Estate Sales Strategy</li>
                  <li>Buyer Qualification</li>
                  <li>Sales Optimization</li>
                </ul>
                <ul>
                  <li>Demand Generation</li>
                  <li>Site Visit Coordination</li>
                  <li>Market Research</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="card-right">
            <h1>Get in Touch</h1>
            <p className="desc">
              Reach out to discuss your real estate project and accelerate your
              sales.
            </p>

            <div className="info-box">
              <div className="icon">
                <Phone size={20} />
              </div>
              <div>
                <h4>Phone</h4>
                <p>
                  <a href="tel:+917075258932">+91 7075258932</a>
                </p>
              </div>
            </div>

            <div className="info-box">
              <div className="icon">
                <Mail size={20} />
              </div>
              <div>
                <h4>Email</h4>
                <p>
                  <a href="mailto:info@presigniarealty.com">
                    info@presigniarealty.com
                  </a>
                </p>
              </div>
            </div>

            <div className="info-box">
              <div className="icon">
                <MapPin size={20} />
              </div>
              <div>
                <h4>Location</h4>
                <p>
                  <a
                    href="https://maps.google.com/?q=Hyderabad"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Hyderabad
                  </a>
                </p>
              </div>
            </div>

            <div className="cta-box">
              <h3>Ready to Get Started?</h3>
              <p>Call us directly to discuss your project.</p>

              <a href="tel:+917075258932" className="call-btn">
                <Phone size={18} /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
      <EnquiryForm />
      <MovingServices />
      <section className="carousel-section contact-section" id="properties">
        <p className="section-tag">More Info</p>
        <h2 className="section-heading">Buy Now</h2>
        <div className="gold-line"></div>

        <p className="why-subtext">
          It will be your first step towards owning your dream home. Explore our
          featured properties and find the perfect match for you and your
          family.
        </p>

        <div className="carousel-container">
          <div className="carousel">
            {properties.map((item, index) => (
              <div className="card" key={item.id}>
                <div className="image-box">
                  <img src={item.image} alt={item.name} />
                </div>

                <div className="card-content">
                  <h3>{item.name}</h3>
                  <p className="location">{item.location}</p>

                  {/* NEW DESCRIPTION */}
                  <p className="desc">{item.description}</p>

                  <p className="price">{item.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-left">
            <div className="logo">
              <img className="logo" src="/logo.png" alt="" />
            </div>

            <p className="footer-tagline">
              Accelerating Real Estate Sales Through Qualified Demand
            </p>

            <p className="footer-desc">
              A performance-driven real estate partner dedicated to accelerating
              project sales through qualified demand and structured buyer
              engagement.
            </p>

            <div className="social-icons">
              <div>
                <a
                  href="https://youtube.com/@presigniarealty?si=1cv2pYhm8P4Yx-N9"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Presignia Realty on YouTube"
                >
                  <FontAwesomeIcon
                    icon={faYoutube}
                    style={{ color: "#FF0000", fontSize: "26px" }}
                  />
                </a>
              </div>
              <div>
                <a
                  href="https://www.facebook.com/share/18AtazayUe/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                >
                  <FontAwesomeIcon
                    icon={faFacebook}
                    style={{ color: "#1877F2", fontSize: "24px" }}
                  />
                </a>
              </div>
              <div>
                <a
                  href="https://www.linkedin.com/company/presignia-realty/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Presignia Realty on LinkedIn"
                >
                  <FontAwesomeIcon
                    icon={faLinkedin}
                    style={{ color: "#0A66C2", fontSize: "24px" }}
                  />
                </a>
              </div>
            </div>
          </div>

          <div className="footer-links">
            <h3>Quick Links</h3>
            <ul>
              <li>
                <a href="#home">Home</a>
              </li>
              <li>
                <a href="#about">About</a>
              </li>
              <li>
                <a href="#services">Services</a>
              </li>
              <li>
                <a href="#whyus">Why Us</a>
              </li>
              <li>
                <a href="#connect">Contact</a>
              </li>
            </ul>
          </div>

          <div className="footer-contact">
            <h3>Contact Us</h3>

            <p>📞 +91 7075258932</p>
            <p>✉️ info@presigniarealty.com</p>
            <p>📍 Hyderabad</p>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="line"></div>
          <p>© 2026 Presignia Realty. All rights reserved.</p>
          <span>
            We don't just generate leads – we drive real estate sales outcomes.
          </span>
        </div>
      </footer>
    </div>
  );
}

export default App;
